import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery, queryDelete, queryGet, queryPost, queryPut } from '@/utils';

export const childApi = createApi({
  reducerPath: 'childApi',
  baseQuery,
  endpoints: (builder) => ({
    searchChildren: builder.mutation({
      query: (body) => queryPost('/children/search', body),
    }),
    quickSearchChildren: builder.mutation({
      query: (body) => queryPost('/children/search/quick', body),
    }),
    getChildSearchResults: builder.query({
      query: ({ searchId, pageNumber = 1, pageSize = 10 }) =>
        queryGet(`/children/search/${searchId}`, { pageNumber, pageSize }),
    }),
    getChildSearchDownload: builder.mutation({
      query: ({ searchId }) => queryGet(`/children/search/${searchId}/download/excel`),
    }),

    getChildrenByStatus: builder.query({
      query: ({ statusKey, pageNumber = 1, pageSize = 10, reportType, adoptionLiaisonId }) =>
        queryGet(`/children/status/${statusKey}`, { pageNumber, pageSize, reportType, adoptionLiaisonId }),
    }),
    downloadChildrenByStatus: builder.mutation({
      query: ({ statusKey, reportType }) => {
        const endpoint = `/children/status/${statusKey}/download/excel`;
        const params = { reportType };

        return queryPost(endpoint, {}, params);
      },
    }),

    getChild: builder.query({
      query: ({ childId }) => queryGet(`/children/${childId}`),
      transformResponse: (response) => {
        const rrc = response.regionalRecruitmentCoordinator ?? {};

        const name = rrc.name ?? {};
        const firstIsNull = name.first === null || name.first === undefined;

        return {
          ...response,
          regionalRecruitmentCoordinator: {
            ...rrc,
            name: firstIsNull
              ? {
                  first: "Not yet",
                  middle: null,
                  last: "assigned",
                  suffix: null,
                }
              : name,
            emailAddress: rrc.emailAddress ?? "iap@fireflyin.org",
          },
        };
      },
    }),
    saveChild: builder.mutation({
      query: ({ childId, body }) => (childId ? queryPut(`/children/${childId}`, body) : queryPost('/children', body)),
    }),
    updateChildStatus: builder.mutation({
      query: ({ childId, ...body }) => queryPut(`/children/${childId}/status`, body),
    }),
    updateChildSummaryPlaceholder: builder.mutation({
      query: ({ childId, ...body }) => queryPut(`/children/${childId}/childsummaryplaceholder`, body),
    }),
    uploadChildSummary: builder.mutation({
      query: ({ childId, body }) => queryPost(`/children/${childId}/summary/upload`, body),
    }),
    deleteChildSummary: builder.mutation({
      query: ({ childId, summaryId }) => queryDelete(`/children/${childId}/summary/${summaryId}`),
    }),
    getChildPhotos: builder.query({
      query: ({ childId }) => queryGet(`/children/${childId}/photos`),
    }),
    setChildPrimaryPhoto: builder.mutation({
      query: ({ childId, photoId }) => queryGet(`/children/${childId}/photos/${photoId}/set-primary`),
    }),
    uploadChildPhoto: builder.mutation({
      query: ({ childId, body }) => queryPost(`/children/${childId}/photos/upload`, body),
    }),
    deleteChildPhoto: builder.mutation({
      query: ({ childId, photoId }) => queryDelete(`/children/${childId}/photos/${photoId}`),
    }),
    getChildInquiries: builder.query({
      query: ({ childId, pageNumber = 1, pageSize = 10 }) =>
        queryGet(`/children/${childId}/inquiries`, { pageNumber, pageSize }),
    }),
    getChildSiblingGroup: builder.query({
      query: ({ childId }) => queryGet(`/children/${childId}/sibling-group`),
    }),
    getChildEvents: builder.query({
      query: ({ childId, pageNumber = 1, pageSize = 10 }) =>
        queryGet(`/children/${childId}/events`, { pageNumber, pageSize }),
    }),
    getChildDocuments: builder.query({
      query: ({ childId, pageNumber = 1, pageSize = 10 }) =>
        queryGet(`/children/${childId}/documents/all`, { pageNumber, pageSize }),
    }),
    uploadChildDocument: builder.mutation({
      query: ({ childId, type, body }) => queryPost(`/children/${childId}/documents/${type}/upload`, body),
    }),
    deleteChildDocument: builder.mutation({
      query: ({ childId, documentId }) => queryDelete(`/children/${childId}/documents/${documentId}`),
    }),
    getChildInformedConsent: builder.query({
      query: ({ childId, pageNumber = 1, pageSize = 10 }) =>
        queryGet(`/children/${childId}/informed-consent`, { pageNumber, pageSize }),
    }),
    updateChildInformedConsent: builder.mutation({
      query: ({ childId, id, body }) => queryPut(`/children/${childId}/informed-consent/${id}`, body),
    }),
    getChildActivities: builder.query({
      query: ({ childId, pageNumber = 1, pageSize = 10 }) =>
        queryGet(`/children/${childId}/activities`, { pageNumber, pageSize }),
    }),
    addChildActivity: builder.mutation({
      query: ({ childId, body }) => queryPost(`/children/${childId}/activities`, body),
    }),
    getChildTasks: builder.query({
      query: ({ childId, pageNumber = 1, pageSize = 10 }) =>
        queryGet(`/children/${childId}/tasks`, { pageNumber, pageSize }),
    }),
    addChildTask: builder.mutation({
      query: ({ childId, body }) => queryPost(`/children/${childId}/tasks`, body),
    }),
    updateChildTask: builder.mutation({
      query: ({ childId, status, body }) => queryPost(`/children/${childId}/tasks/${status}`, body),
    }),
    updateChildTaskNote: builder.mutation({
      query: ({ childId, id, body }) => queryPost(`/children/${childId}/tasks/${id}/note`, body),
    }),
    updateChildSubTask: builder.mutation({
      query: (data) =>
        queryPost(`/children/${data.childId}/subtasks/${data.status}`, {
          assignment: data.assignment,
          type: { id: data.typeId },
        }),
    }),
    addChildTaskReferral: builder.mutation({
      query: ({ childId, body }) => queryPost(`/children/${childId}/tasks/add-referral`, body),
    }),
    getChildPlacements: builder.query({
      query: ({ childId, pageNumber = 1, pageSize = 10 }) =>
        queryGet(`/children/${childId}/placements`, { pageNumber, pageSize }),
    }),
    placeWithParents: builder.mutation({
      query: ({ childId, parentId, note, datePlaced, dateMatched }) => queryPost(`/children/${childId}/placements`, { parentId, note, datePlaced, dateMatched }),
    }),
    getChildAdoptions: builder.query({
      query: ({ childId, pageNumber = 1, pageSize = 10 }) =>
        queryGet(`/children/${childId}/adoptions`, { pageNumber, pageSize }),
    }),
    setAdoptions: builder.mutation({
      query: (data) => queryPost(`/children/${data.childId}/adoptions`, data),
    }),
  }),
});

export const {
  useSearchChildrenMutation,
  useQuickSearchChildrenMutation,
  useGetChildSearchResultsQuery,
  useGetChildSearchDownloadMutation,

  useGetChildrenByStatusQuery,
  useDownloadChildrenByStatusMutation,

  useGetChildQuery,
  useSaveChildMutation,
  useUpdateChildStatusMutation,
  useUpdateChildSummaryPlaceholderMutation,
  useUploadChildSummaryMutation,
  useDeleteChildSummaryMutation,
  useGetChildPhotosQuery,
  useSetChildPrimaryPhotoMutation,
  useUploadChildPhotoMutation,
  useDeleteChildPhotoMutation,
  useGetChildInquiriesQuery,
  useGetChildSiblingGroupQuery,
  useGetChildEventsQuery,
  useGetChildDocumentsQuery,
  useDeleteChildDocumentMutation,
  useUploadChildDocumentMutation,
  useGetChildInformedConsentQuery,
  useUpdateChildInformedConsentMutation,
  useGetChildActivitiesQuery,
  useAddChildActivityMutation,
  useGetChildTasksQuery,
  useAddChildTaskMutation,
  useUpdateChildTaskMutation,
  useUpdateChildTaskNoteMutation,
  useUpdateChildSubTaskMutation,
  useAddChildTaskReferralMutation,
  useGetChildPlacementsQuery,
  usePlaceWithParentsMutation,
  useMatchWithParentsMutation,
  useGetChildAdoptionsQuery,
  useSetAdoptionsMutation,
} = childApi;
